import { html, type TemplateResult } from 'lit-html'
import { CARDINALS, RELIC_GLYPH, TILE_COUNT, type api } from '@guild/core'

import type { App } from '../app.ts'
import {
  cellsOf,
  glyphClass,
  LEGEND,
  mapGrid,
  SPOKEN_FOR_GLYPH,
  withShores,
  YOU_GLYPH,
} from '../map.ts'
import { courseOf, loadDay, noteIsland, openDay, routeForStep } from '../day.ts'
import { inboxStore } from '../inbox.ts'
import { letterSentOn, loadLetterSent } from '../letter-sent.ts'
import { defineScreen } from '../screen.ts'
import { focusSlip, practiceSlip, rememberFocus, restoreFocus, slip as note } from '../slip.ts'
import type { Store } from '../store.ts'
import {
  dayLabel,
  dayOrdinal,
  directionLabel,
  errorText,
  fragmentText,
  islandName,
  logLine,
  relic as relicEntry,
  relicName,
} from '../text.ts'
import { reveal, stopReveal } from '../typewriter.ts'

/**
 * The island (#54).
 *
 * The client decides nothing here. It sends a direction; the server answers
 * with where the player now stands, what it cost, what that showed and a log
 * event, and this screen draws exactly that. What it may draw is what the
 * state response carries: the player's own seen tiles and the hearsay they
 * charted, never the island.
 *
 * Three budgets arrive through the same state: `live` on the rising day,
 * `return` on the sinking day -- ten stamina to chase what a letter claimed --
 * and `replay` on a finalised island, which has no limit and keeps nothing.
 *
 * The return trip begins by coming ashore. Until the player has chosen one of
 * the island's landing shores the screen has nothing to move: the shores are
 * pickable on the map and listed as coordinates beside it, and the four-way
 * control and the arrow keys are inert (Q21.1). The server refuses a move made
 * before then in any case, because the client decides nothing.
 *
 * The morning after (#133, Q25.2; #135, Q27.1): on the first visit of a day,
 * the player is put on the current step of the day's course rather than on
 * today's island -- the post, yesterday's sinking island, the letter -- and
 * on yesterday's island is told whether the post has brought letters about
 * it. The rule is `day.ts`'s, read from the Atlas and the inbox it already
 * fetches.
 */

const KEYS: Readonly<Record<string, keyof typeof CARDINALS>> = {
  ArrowUp: 'n',
  ArrowRight: 'e',
  ArrowDown: 's',
  ArrowLeft: 'w',
}

/** The four-way control, in the order it is laid out. */
const CONTROL: readonly (keyof typeof CARDINALS | null)[] = [
  null,
  'n',
  null,
  'w',
  null,
  'e',
  null,
  's',
  null,
]

const BAR_CELLS = 10
/** Where the first day is told what the rest of its strength is for. */
const TEN_LEFT = 10
const FULL = '▮'
const EMPTY = '▯'

/**
 * What a step turned up, held until it is read (#101).
 *
 * A ruin gives a fragment and a relic gives a name and a description, and both
 * arrive while the player is looking at the map with a finger on the next
 * arrow key. Neither can be had twice, so neither goes past in a line.
 */
type Found =
  | { readonly kind: 'ruin'; readonly fragmentId: string; readonly note?: string }
  | {
      readonly kind: 'relic'
      readonly relicId: string
      readonly spokenFor: boolean
      readonly note?: string
    }
  /** The morning's slip: how many letters the post brought about this island (#133). */
  | { readonly kind: 'morning'; readonly delivered: number }
  /**
   * The practice shore's word on what just happened (#134): a `ui` key, and
   * whether the post is the next thing to do. Alone when the step turned up
   * nothing else; otherwise it rides on the ruin's or the relic's slip as a
   * note, so one moment hands over one piece of paper.
   */
  | { readonly kind: 'practice'; readonly key: string; readonly toThePost: boolean }

type State = {
  island: api.ApiIslandState | null
  loading: boolean
  problem: string | null
  /** The line under the map, and how much of it the typewriter has shown. */
  line: string | null
  shown: number
  /** A second line, for a contradiction the step turned up. */
  aside: string | null
  found: Found | null
  moving: boolean
  /**
   * The island whose first-landing guidance is running (#95, Q19.1).
   *
   * Kept for the session rather than asked for again: `firstLanding` is true
   * only until the landing writes itself down, so a player who walks over to
   * the letter and back would otherwise lose the rest of the guidance.
   */
  guidedDay: string | null
  /** Guidance already said. No line twice, and none on a later island. */
  said: readonly string[]
  /** Whether this sinking island is the earliest the player has walked. */
  firstReturn: boolean
  returnChecked: boolean
  /** The sinking island the morning is sending the player back to, until they land on it. */
  morningFor: string | null
}

const initial: State = {
  island: null,
  loading: true,
  problem: null,
  line: null,
  shown: 0,
  aside: null,
  found: null,
  moving: false,
  guidedDay: null,
  said: [],
  firstReturn: false,
  returnChecked: false,
  morningFor: null,
}

const bar = (share: number): string => {
  const filled = Math.max(0, Math.min(BAR_CELLS, Math.round(share * BAR_CELLS)))
  return `${FULL.repeat(filled)}${EMPTY.repeat(BAR_CELLS - filled)}`
}

const percent = (app: App, share: number): string =>
  new Intl.NumberFormat(app.locale, { style: 'percent' }).format(share)

export const islandScreen = defineScreen<State>({
  id: 'island',
  initial,

  async enter(app, route, store, current) {
    stopReveal()
    store.set({ loading: true, problem: null, line: null, aside: null, found: null })
    const day = route.screen === 'island' ? route.day : null
    const result = await app.api.island(day ?? undefined)
    if (!current()) return
    if (!result.ok) {
      if (result.status === 401) await app.setSession(null)
      if (current()) store.set({ loading: false, problem: errorText(app, result), island: null })
      return
    }
    const island = result.value

    // The day's course (#135), which is where the morning after (#133) now
    // lives. An ask for today's island fills the shared day store -- once,
    // or again when this is a day it has not seen, which is how a tab left
    // open overnight learns of the letters that arrived (#116) -- and the
    // first such ask of a game day opens the day on its current step: the
    // post, yesterday's sinking island, the letter. Decided before today's
    // island is shown, so the screen does not draw one island and then swap
    // it for another. Any ask is a fresher copy of an island the store holds.
    if (day === null) {
      await loadDay(app, island)
      if (!current()) return
      const opening = openDay()
      const course = courseOf()
      if (opening !== null && opening !== 'rising' && course !== null) {
        // The morning's slip waits on the island the morning sends the
        // player back to (#133).
        if (opening === 'sinking') store.set({ morningFor: course.yesterday })
        app.go(routeForStep(opening, course))
        return
      }
    }
    noteIsland(island)

    // A first landing is a fact about this ask, and the ask is not repeated;
    // the day it happened on is what the rest of the guidance hangs from.
    const guidedDay =
      island.firstLanding && island.budget === 'live' ? island.day : store.get().guidedDay
    store.set({ loading: false, island, guidedDay })

    // The morning's slip, on the island the morning sent the player back to:
    // how many letters the post has brought about it, with the post one key
    // away, once (#133). The mark beside `[ The Post ]` stays as the reminder.
    if (store.get().morningFor === island.day) {
      const delivered = inboxStore
        .get()
        .letters.filter((letter) => letter.dayAbout === island.day).length
      store.set({ morningFor: null, found: { kind: 'morning', delivered } })
      focusSlip()
    }

    // Whether today's letter has gone, for the offer a spent day makes (#102).
    // Only on the live day: the letter is about the island that is up now, and
    // yesterday's sinking ground says nothing about it.
    if (island.budget === 'live') await loadLetterSent(app, island.day)
    if (!current()) return

    // The first sinking morning has no flag of its own: it is the morning on
    // which the island going under is the earliest the player has ever walked.
    if (island.budget === 'return' && !store.get().returnChecked) {
      const atlas = await app.api.atlas()
      if (!current()) return
      const earliest = atlas.ok
        ? [...atlas.value.islands].map((entry) => entry.day).sort()[0]
        : undefined
      store.set({
        returnChecked: true,
        firstReturn: earliest === undefined || earliest >= island.day,
      })
    }

    // The practice shore's word on the morning, or on the landing (#134): the
    // shore says each once, and nothing at all off it. The morning's slip
    // carries the way to the post, because the letter is the point of it.
    const practised = practiceSlip(
      app,
      choosingShore(island) ? 'practiceMorning' : 'practiceLanding',
    )
    if (practised !== null) {
      store.set({
        found: { kind: 'practice', key: practised, toThePost: practised === 'practiceMorning' },
      })
      focusSlip()
    }

    // The morning that owes a landing says so, and says nothing else: the
    // island's own line waits until there is somebody standing on it.
    if (choosingShore(island)) {
      say(app, store.set, app.t('returnChooseShore'))
      return
    }
    const landing = guidance(store, island, 'firstDayLanding')
    say(app, store.set, landing === null ? logLine(app, island.log) : app.t(landing))
  },

  keydown(event, state, app, store) {
    // The slip is read and put down; it never holds the next step for long.
    if (state.found !== null) {
      if (event.key === 'Escape') {
        event.preventDefault()
        dismiss(store)
        return
      }
      if (KEYS[event.key] !== undefined) dismiss(store)
    }

    const direction = KEYS[event.key]
    if (direction === undefined || state.island === null) return
    // Nothing to move until the boat has put in (Q21.1).
    if (choosingShore(state.island)) return
    if (event.metaKey || event.ctrlKey || event.altKey) return
    const target = event.target as HTMLElement | null
    if (target !== null && (target.tagName === 'INPUT' || target.tagName === 'SELECT')) return
    event.preventDefault()
    void step(app, store, state, direction)
  },

  render(state, app, store) {
    if (state.island === null) {
      return html`<section class="screen">
        ${state.problem === null ? null : html`<p class="problem" role="alert">${state.problem}</p>`}
      </section>`
    }

    const island = state.island
    const choosing = choosingShore(island)
    // At sea, so no `@` on yesterday's tile; the shores are what there is to
    // pick, and they are drawn over the fog as the sand they are (Q21.1).
    const cells = choosing
      ? withShores(cellsOf(island, { you: false }), island.shores)
      : cellsOf(island)
    const seen = island.cells.filter((cell) => cell.source === 'seen').length
    const hearsay = island.cells.filter((cell) => cell.source === 'hearsay').length
    const stamina = island.stamina
    const total = island.staminaTotal

    return html`<section class="screen screen-island">
      <header class="island-head">
        <div class="seal seal-small" aria-hidden="true">✦</div>
        <div>
          <h1 class="title">${islandName(app, island.islandName)}</h1>
          <p class="meta">
            ${dayLabel(app, island.dayNumber)}
            ${island.dayNumber === null ? null : html`· ${dayOrdinal(app, island.dayNumber)}`}
          </p>
        </div>
      </header>

      <!-- The log belongs to the step just taken, so it sits where the eye
           already is: above the map, not under it as a footnote (#98).

           The typewritten copy is for the eye only. A live region whose text
           changes every 18 ms is re-announced on every change, so a screen
           reader would hear each log line as a queue of near-duplicate
           fragments; the whole line goes once into the offscreen sibling,
           which is the region that speaks (#113). -->
      <p class="log" aria-hidden="true">
        ${state.line === null ? null : state.line.slice(0, state.shown)}
        <span class="caret" aria-hidden="true"></span>
      </p>
      <p class="offscreen" aria-live="polite">${state.line ?? ''}</p>
      ${state.aside === null ? null : html`<p class="log log-aside">${state.aside}</p>`}
      ${mapGrid(
        cells,
        choosing
          ? {
              onPoint: (x, y, phase) => {
                if (phase !== 'down') return
                const shore = island.shores.find((at) => at.at.x === x && at.at.y === y)
                if (shore !== undefined) void comeAshore(app, store, store.get(), shore.index)
              },
            }
          : {},
      )}
      ${shoreList(app, store, state, island)}

      <p class="status">
        ${
          // Replay has no budget at all, so it has no line here either (Q1.2).
          stamina === null || total === null
            ? null
            : html`<span
                >${app.t('staminaLabel')}
                <span class="bar">${bar(stamina / total)}</span> ${stamina}/${total}</span
              >`
        }
        <span>${app.t('chartedLabel')} ${percent(app, seen / TILE_COUNT)}</span>
        <span class="accent"
          >${app.t('hearsayLabel')} ${app.t('hearsayTiles', { n: hearsay })}</span
        >
        <!-- Nowhere yet: a player still choosing a shore is at sea, and the
             position on the state is where yesterday left them (Q21.1). -->
        ${
          choosing
            ? null
            : html`<span
                >${app.t('youAreAt', { x: island.position.x, y: island.position.y })}</span
              >`
        }
      </p>

      <!-- The four-way control, directly under what is left of the day (#123).
           At 360 px it used to sit below the legend and the hint, about 780 px
           down, so a phone player tapped it with the map off the top of the
           screen; here the map, the stamina row and the pad share one
           viewport and the key to the map follows them.

           aria-disabled and not disabled: a disabled button loses the focus
           to the body, and every step set it for as long as the move was in
           flight, so a keyboard player on the pad lost their place after
           each one. The refusal is in the handler instead (#118). -->
      <div class="pad">
        ${CONTROL.map((direction) =>
          direction === null
            ? html`<span></span>`
            : html`<button
                class="pad-key"
                aria-label=${directionLabel(app, direction)}
                aria-disabled=${state.moving || choosing ? 'true' : 'false'}
                @click=${() => void press(app, store, direction)}
              >
                ${{ n: '↑', e: '→', s: '↓', w: '←' }[direction]}
              </button>`,
        )}
      </div>

      ${slip(app, store, state)}
      ${state.problem === null ? null : html`<p class="problem" role="alert">${state.problem}</p>`}
      ${budgetNote(app, state, island)}

      <div class="controls">
        <div class="legend">
          <p>
            ${LEGEND.map(
              (entry) =>
                html`<span class="legend-item"
                  ><span class=${glyphClass(entry.glyph)}>${entry.glyph}</span>
                  ${app.t(entry.key)}</span
                >`,
            )}
          </p>
          <p>
            <span class="legend-item"
              ><span class="glyph">${YOU_GLYPH}</span> ${app.t('legendYou')}</span
            >
          </p>
          <!-- The three ways a tile can be known are one list; the player's
               own glyph and its meaning are another (#99). -->
          <p class="legend-states">
            <span class="legend-item">${app.t('legendSeen')}</span> ·
            <span class="legend-item accent">${app.t('legendHearsay')}</span> ·
            <span class="legend-item quiet-ink">${app.t('legendUnknown')}</span>
          </p>
          <p class="hint">${app.t('movementHint')}</p>
        </div>
      </div>

      ${dayNote(app, state, island)} ${relicNotes(app, island)}
    </section>`
  },
})

// --- the guided first landing (#95, Q19.1) -----------------------------------

/**
 * Whether the island is still owed a landing: the sinking day, before the boat
 * has put in (Q21.1). One field, because the server decides it.
 */
const choosingShore = (island: api.ApiIslandState): boolean => !island.returnLanded

/**
 * Whether this island is the one the player is being shown around.
 *
 * Only their first, and only while they are on it: a later island says nothing
 * extra, and neither does yesterday's when they come back to it.
 */
const isGuided = (state: State, island: api.ApiIslandState): boolean =>
  state.guidedDay !== null && state.guidedDay === island.day

/**
 * Claim a guidance line, or nothing.
 *
 * Returns the key the first time its moment comes and `null` ever after, and
 * writes it down as it goes, so no line is said twice. The set is the store's
 * and lasts the session: a player who is shown around twice in one afternoon
 * has been shown around twice, which is worse than showing them once.
 */
function guidance(store: Store<State>, island: api.ApiIslandState, key: string): string | null {
  const state = store.get()
  if (!isGuided(state, island) || state.said.includes(key)) return null
  store.set({ said: [...state.said, key] })
  return key
}

// --- what a step turned up ---------------------------------------------------

/** Put the slip down, and give the focus back to whatever had it. */
function dismiss(store: Store<State>): void {
  store.set({ found: null })
  restoreFocus()
}

/**
 * The slip: a ruin's fragment, or a relic's name and what it is (#101).
 *
 * It is the one thing on this screen that asks to be read before the next
 * step, because it is the one thing that cannot be had twice. Escape puts it
 * down, so does its own verb, and so does an arrow key -- walking on is a way
 * of saying "noted" too.
 *
 * On the practice shore the guild's word on the moment rides on the same
 * paper, under the fragment or the relic (#134).
 */
function slip(app: App, store: Store<State>, state: State): TemplateResult | null {
  const found = state.found
  if (found === null) return null
  if (found.kind === 'morning') return morningSlip(app, store, found.delivered)
  if (found.kind === 'practice') return practiceNote(app, store, found)

  const entry = found.kind === 'relic' ? relicEntry(app, found.relicId) : undefined
  const body =
    found.kind === 'ruin'
      ? html`<p class="serif-note">${fragmentText(app, found.fragmentId)}</p>`
      : html`<p class="slip-name">
            <span class="glyph accent">${RELIC_GLYPH}</span> ${relicName(app, found.relicId)}
          </p>
          ${entry === undefined ? null : html`<p class="serif-note">${entry.description}</p>`}
          <p class="meta">${app.t(found.spokenFor ? 'relicSpokenFor' : 'relicStaked')}</p>`

  return note(app, {
    label: found.kind === 'ruin' ? 'foundRuin' : 'foundRelic',
    body: html`${body}
    ${found.note === undefined ? null : html`<p class="note">${app.t(found.note)}</p>`}`,
    verbs: [{ key: 'foundNoted', primary: true, press: () => dismiss(store) }],
  })
}

/**
 * The practice shore's slip on its own (#134): the landing, the first step,
 * the sea, the morning, the contradiction. The morning's carries `[ The Post ]`
 * as the real morning's does (#133), because the letter is what it is for.
 */
function practiceNote(
  app: App,
  store: Store<State>,
  found: { readonly key: string; readonly toThePost: boolean },
): TemplateResult {
  return note(app, {
    body: app.t(found.key),
    verbs: [
      ...(found.toThePost
        ? [
            {
              key: 'navPost',
              primary: true,
              press: () => {
                dismiss(store)
                app.go({ screen: 'post', publicId: null })
              },
            },
          ]
        : []),
      { key: 'foundNoted', press: () => dismiss(store) },
    ],
  })
}

/**
 * The morning's slip (#133): the post has brought so many letters about this
 * island, or none. `[ The Post ]` is the way to them; `[ noted ]` puts the
 * slip down, because a slip a finger cannot dismiss is a wall. Nothing to go
 * to when nothing came, so then there is only the one key.
 */
function morningSlip(app: App, store: Store<State>, delivered: number): TemplateResult {
  const said = delivered === 0 ? app.t('morningNoPost') : app.t('morningPost', { n: delivered })
  return html`<div class="slip" role="dialog" aria-label=${said}>
    <p class="serif-note">${said}</p>
    <p class="row">
      ${
        delivered === 0
          ? null
          : html`<button
              class="verb verb-primary slip-verb"
              @click=${() => {
                dismiss(store)
                app.go({ screen: 'post', publicId: null })
              }}
            >
              [ ${app.t('navPost')} ]
            </button>`
      }
      <button class="verb slip-verb" @click=${() => dismiss(store)}>
        [ ${app.t('foundNoted')} ]
      </button>
    </p>
  </div>`
}

// --- coming ashore (Q21.1) ---------------------------------------------------

/**
 * The shores, as coordinates.
 *
 * The map is `aria-hidden` -- it is a grid of glyphs and says nothing useful a
 * character at a time -- so the list is the keyboard's route to the same
 * choice, and it reads the way a letter quotes a position: `x, y`.
 */
function shoreList(
  app: App,
  store: Store<State>,
  state: State,
  island: api.ApiIslandState,
): TemplateResult | null {
  if (!choosingShore(island)) return null
  return html`<ul class="shore-list" aria-label=${app.t('returnChooseShore')}>
    ${island.shores.map(
      (shore) =>
        html`<li>
          <button
            class="verb shore-verb"
            ?disabled=${state.moving}
            @click=${() => void comeAshore(app, store, store.get(), shore.index)}
          >
            [ ${shore.at.x}, ${shore.at.y} ]
          </button>
        </li>`,
    )}
  </ul>`
}

/**
 * Put in at a shore. Once: the choice cannot be changed, and the server is
 * what says so -- this only asks (Q21.1).
 */
async function comeAshore(
  app: App,
  store: Store<State>,
  state: State,
  shoreIndex: number,
): Promise<void> {
  const island = state.island
  if (island === null || state.moving || !choosingShore(island)) return
  // Choosing a shore is a way of saying "noted" too (#133).
  store.set({ moving: true, problem: null, found: null })

  const result = await app.api.returnLanding(island.day, shoreIndex)
  if (!result.ok) {
    store.set({ moving: false })
    say(app, store.set, errorText(app, result))
    return
  }
  const landed = result.value
  store.set({ moving: false, island: landed })
  noteIsland(landed)
  say(app, store.set, app.t('returnLanded', { x: landed.position.x, y: landed.position.y }))
}

/** What the island has to say about itself: the return trip, replay, the seed. */
function budgetNote(app: App, state: State, island: api.ApiIslandState): TemplateResult | null {
  const notes: string[] = []
  // The return trip's own lines belong to somebody standing on the island, so
  // they wait for the landing; `firstReturn` most of all (Q20.1, Q21.1).
  if (island.budget === 'return' && island.returnLanded) {
    notes.push(app.t(state.firstReturn ? 'firstReturn' : 'returnBudgetIntro'))
  }
  if (island.budget === 'replay') notes.push(app.t('replayIntro'))
  if (island.seedHex !== undefined) notes.push(app.t('seedPublished'))
  if (notes.length === 0) return null
  return html`<div class="notes">
    ${notes.map((note) => html`<p class="note">${note}</p>`)}
    ${island.seedHex === undefined ? null : html`<p class="seed">${island.seedHex}</p>`}
  </div>`
}

/**
 * What is left of the day, said where the day's notes are.
 *
 * The end of a day is not a footnote to the legend: the legend is a key to the
 * map and says nothing about this player, so the news that the walking is over
 * belongs under the map with the relic notes -- and the one thing still worth
 * doing sits at the right edge of it, where a hand looks for a verb.
 */
function dayNote(app: App, state: State, island: api.ApiIslandState): TemplateResult | null {
  // The server says when the day is over: nothing affordable is not the same
  // as nothing left (Q20.2).
  if (!island.spent) return null
  return html`<div class="day-note">
    <p class="warn">
      ${app.t(isGuided(state, island) ? 'firstDayExhausted' : 'movementExhausted')}
    </p>
    ${
      // What is left of the day is the letter -- unless it has already gone
      // out with the tide (#102).
      island.budget === 'live' && !letterSentOn(island.day)
        ? html`<button
            class="verb verb-primary verb-end"
            @click=${() => app.go({ screen: 'letter' })}
          >
            [ ${app.t('navLetter')} ]
          </button>`
        : null
    }
  </div>`
}

/**
 * The relics this player knows of, with the coordinate a letter would quote.
 *
 * `staked` is read before `spokenFor`, because `spokenFor` is true of the
 * player's own stake as well (Q15.5): your own cord is `◆`, and `◇` is
 * somebody else's.
 */
function relicNotes(app: App, island: api.ApiIslandState): TemplateResult | null {
  if (island.relics.length === 0) return null
  return html`<ul class="relic-notes">
    ${island.relics.map(
      (relic) =>
        html`<li>
          <!-- The coordinate is what a letter quotes, so it is not left to run
               into the name; the status gets a line of its own (#104). -->
          <p class="relic-line">
            <span class="glyph ${relic.staked || !relic.spokenFor ? 'accent' : 'quiet-ink'}"
              >${relic.staked || !relic.spokenFor ? RELIC_GLYPH : SPOKEN_FOR_GLYPH}</span
            >
            <span class="coord">${relic.at.x}, ${relic.at.y}</span>
            ${
              relic.relicId === undefined
                ? null
                : html`<span class="relic-title">${relicName(app, relic.relicId)}</span>`
            }
          </p>
          ${
            relic.staked
              ? html`<p class="meta">${app.t('relicStaked')}</p>`
              : relic.spokenFor
                ? html`<p class="meta">${app.t('relicSpokenFor')}</p>`
                : null
          }
        </li>`,
    )}
  </ul>`
}

/**
 * A press of the four-way control.
 *
 * The button is never `disabled`, so it says no here instead: nothing to move
 * before the boat has put in (Q21.1), and nothing while a step is in flight
 * -- which `step` refuses again in any case (#118).
 */
async function press(
  app: App,
  store: Store<State>,
  direction: keyof typeof CARDINALS,
): Promise<void> {
  const state = store.get()
  if (state.island === null || state.moving || choosingShore(state.island)) return
  await step(app, store, state, direction)
}

type Setter = (patch: Partial<State>) => void

/** Put a line under the map, one character at a time unless motion is off. */
function say(app: App, set: Setter, line: string | null): void {
  if (line === null) {
    stopReveal()
    set({ line: null, shown: 0 })
    return
  }
  set({ line, shown: 0 })
  reveal(line, app.reducedMotion, (shown) => set({ shown }))
}

async function step(
  app: App,
  store: Store<State>,
  state: State,
  direction: keyof typeof CARDINALS,
): Promise<void> {
  const island = state.island
  if (island === null || state.moving) return
  // Before the step, because the control that was pressed is still the thing
  // the focus should go back to once the slip has been read.
  rememberFocus()
  store.set({ moving: true, problem: null, aside: null, found: null })

  const result = await app.api.move(island.day, direction)
  if (!result.ok) {
    // A refusal carries its own log event where one fits, so the island says
    // why in the guild's voice rather than showing a box.
    store.set({ moving: false })
    if (result.log === undefined) {
      store.set({ problem: errorText(app, result) })
      return
    }
    // The sea refuses a first-day walker in more words than it refuses anyone
    // else, and only the once.
    const refused =
      result.log.event === 'water-edge' ? guidance(store, island, 'firstDayRefusal') : null
    say(app, store.set, refused === null ? errorText(app, result) : app.t(refused))
    // On the practice shore, the first refusal by the sea is a slip (#134).
    const practised = result.log.event === 'water-edge' ? practiceSlip(app, 'practiceSea') : null
    if (practised !== null) {
      store.set({ found: { kind: 'practice', key: practised, toThePost: false } })
      focusSlip()
    }
    return
  }

  const move = result.value
  const merged = mergeCells(island.cells, move.revealed)
  const found = withPracticeNote(app, move, foundIn(move))
  const walked: api.ApiIslandState = {
    ...island,
    position: move.position,
    stamina: move.stamina,
    spent: move.spent,
    cells: merged,
    // What the step brought into sight, then the tile it stood on, so a
    // relic seen from two tiles away is drawn as one the moment it is (#129)
    // and the stake this step wrote wins over the marker that first saw it.
    relics: mergeRelics(island.relics, [...move.relics, ...(move.relic ? [move.relic] : [])]),
  }
  // The course follows the day as it is spent (#135).
  noteIsland(walked)
  store.set({
    moving: false,
    island: walked,
    found,
    // The second line is for a contradiction the step's own event did not
    // report -- one that came with a relic or a ruin. When the event is the
    // contradiction, the line under the map already says it, and the same
    // seed picks the same words, so saying it twice said nothing more.
    aside:
      move.contradicted.length === 0 || move.log.event === 'hearsay-false'
        ? null
        : (app.content.logLine(app.locale, 'hearsay-false', move.log.seed) ?? null),
  })
  // A first day says what the step meant before it says what it looked like.
  const taught =
    (move.revealed.length > 0 ? guidance(store, island, 'firstDayFirstStep') : null) ??
    (move.stamina !== null && move.stamina <= TEN_LEFT
      ? guidance(store, island, 'firstDayTenLeft')
      : null)
  say(app, store.set, taught === null ? logLine(app, move.log) : app.t(taught))
  if (found !== null) focusSlip()
}

/**
 * The practice shore's word on a step (#134), if it has one for this one.
 *
 * One moment, one piece of paper: where the step also turned up a fragment or
 * a relic, the word rides on that slip as a note; otherwise it is the slip.
 * The ground's verdict comes first, because it is the one thing on the shore
 * the player did not go looking for; then the ruin, the relic, and the first
 * ground charted. Off the shore there is nothing to say and nothing changes.
 */
function withPracticeNote(app: App, move: api.ApiMoveResponse, found: Found | null): Found | null {
  const key =
    (move.contradicted.length > 0 ? practiceSlip(app, 'practiceContradiction') : null) ??
    (move.fragmentId !== undefined ? practiceSlip(app, 'practiceRuin') : null) ??
    (move.relic !== undefined ? practiceSlip(app, 'practiceRelic') : null) ??
    (move.revealed.length > 0 ? practiceSlip(app, 'practiceFirstStep') : null)
  if (key === null) return found
  if (found === null) return { kind: 'practice', key, toThePost: false }
  if (found.kind === 'ruin' || found.kind === 'relic') return { ...found, note: key }
  return found
}

/**
 * What this step turned up, if anything.
 *
 * A relic is `spokenFor` in the response whenever anyone has staked it, the
 * player included; the log event is what tells the two apart, because the
 * server only says `relic-spoken-for` when somebody else was there first.
 */
function foundIn(move: api.ApiMoveResponse): Found | null {
  if (move.fragmentId !== undefined) return { kind: 'ruin', fragmentId: move.fragmentId }
  const relic = move.relic
  if (relic?.relicId !== undefined) {
    return {
      kind: 'relic',
      relicId: relic.relicId,
      spokenFor: move.log.event === 'relic-spoken-for',
    }
  }
  return null
}

/** Tiles a step showed, over what the player already had. Seen always wins. */
function mergeCells(
  existing: readonly api.ApiMapCell[],
  revealed: readonly api.ApiMapCell[],
): api.ApiMapCell[] {
  const shown = new Map(existing.map((cell) => [`${cell.x},${cell.y}`, cell]))
  for (const cell of revealed) shown.set(`${cell.x},${cell.y}`, cell)
  return [...shown.values()]
}

/** Markers a step sent, over what the player already had, by index. Later wins. */
function mergeRelics(
  relics: api.ApiIslandState['relics'],
  found: api.ApiIslandState['relics'],
): api.ApiIslandState['relics'] {
  if (found.length === 0) return relics
  const known = new Map(relics.map((relic) => [relic.index, relic]))
  for (const relic of found) known.set(relic.index, relic)
  return [...known.values()]
}
